/**
 * Utility functions for Map tile layers and CARTO basemaps.
 */

export const getCartoTileUrl = (style: 'dark_all' | 'rastertiles/voyager' | 'light_all' = 'dark_all'): string => {
  const apiKey = import.meta.env.VITE_CARTO_API_KEY;
  const baseUrl = `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png`;
  return apiKey ? `${baseUrl}?key=${apiKey}&api_key=${apiKey}` : baseUrl;
};

const getDistanceMeters = (p1: [number, number], p2: [number, number]): number => {
  const R = 6371000;
  const dLat = (p2[0] - p1[0]) * Math.PI / 180;
  const dLon = (p2[1] - p1[1]) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(p1[0] * Math.PI / 180) * Math.cos(p2[0] * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Calculates compass bearing (0-360 deg) from p1 to p2.
 */
export const calculateBearing = (p1: [number, number], p2: [number, number]): number => {
  const lat1 = p1[0] * Math.PI / 180;
  const lon1 = p1[1] * Math.PI / 180;
  const lat2 = p2[0] * Math.PI / 180;
  const lon2 = p2[1] * Math.PI / 180;
  const dLon = lon2 - lon1;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const brng = Math.atan2(y, x) * 180 / Math.PI;
  return (brng + 360) % 360;
};

export interface DirectionArrow {
  lat: number;
  lon: number;
  bearing: number;
}

export interface ArrowMarkerOptions {
  color?: string;
  outlineColor?: string;
  size?: number;
}

/**
 * Computes evenly-spaced direction arrow coordinates and bearings along a path.
 */
export const computePathDirectionArrows = (
  path: [number, number][] | undefined,
  targetCount: number = 24
): DirectionArrow[] => {
  if (!path || path.length < 5) return [];

  const cumDists: number[] = [0];
  for (let i = 1; i < path.length; i++) {
    cumDists.push(cumDists[i - 1] + getDistanceMeters(path[i - 1], path[i]));
  }

  const totalDist = cumDists[cumDists.length - 1];
  if (totalDist < 50) return [];

  // Adapt arrow count to route length (roughly 1 arrow per 750m, bounded)
  const count = Math.min(40, Math.max(6, Math.min(targetCount, Math.round(totalDist / 750))));
  const stepDist = totalDist / (count + 1);

  const arrows: DirectionArrow[] = [];
  let currentIdx = 0;

  for (let a = 1; a <= count; a++) {
    const target = a * stepDist;
    while (currentIdx < cumDists.length - 1 && cumDists[currentIdx + 1] < target) {
      currentIdx++;
    }

    const basePt = path[currentIdx];
    // Smooth bearing by looking ~50m ahead to avoid single-step GPS jitter
    let lookAheadIdx = currentIdx + 1;
    while (lookAheadIdx < path.length - 1 && (cumDists[lookAheadIdx] - cumDists[currentIdx]) < 50) {
      lookAheadIdx++;
    }
    const lookAheadPt = path[lookAheadIdx];

    const bearing = calculateBearing(basePt, lookAheadPt);
    arrows.push({
      lat: basePt[0],
      lon: basePt[1],
      bearing: Math.round(bearing)
    });
  }

  return arrows;
};

/**
 * Creates a non-interactive Leaflet marker displaying a rotated SVG arrowhead.
 */
export const createDirectionArrowMarker = (
  L: any,
  arrow: DirectionArrow,
  options?: ArrowMarkerOptions
) => {
  const color = options?.color || '#ffffff';
  const outlineColor = options?.outlineColor || '#1e1b4b';
  const size = options?.size || 14;
  const boxSize = size + 6;

  const icon = L.divIcon({
    className: 'route-direction-arrow',
    html: `<div style="transform: rotate(${arrow.bearing}deg); width: ${boxSize}px; height: ${boxSize}px; display: flex; align-items: center; justify-content: center; pointer-events: none;">
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="${outlineColor}" stroke-width="2" stroke-linejoin="round" style="filter: drop-shadow(0 1px 3px rgba(0,0,0,0.8)); display: block;">
        <path d="M12 2 L22 20 L12 15 L2 20 Z" />
      </svg>
    </div>`,
    iconSize: [boxSize, boxSize],
    iconAnchor: [boxSize / 2, boxSize / 2]
  });

  return L.marker([arrow.lat, arrow.lon], {
    icon,
    interactive: false,
    keyboard: false
  });
};

